/**
 * Signals tab PR 1 — QA batch 2, unit B2 (`plans/signals-tab-pr1/qa-batch2-plan.md`
 * D-A). The single verification-state resolver: chip, row/Timeline/card
 * buttons, the Step column's label, and the archive-form prefill all derive
 * from `resolveSignalVerificationState` / `getSignalRowActions`'s consumer
 * (`rowActions.ts`) rather than each re-deriving their own read of
 * `signal_status` + `gong_verified_at` + `gong_probe_status` + `signal_tactic`.
 *
 * Pure — importable by both the server (Node tests) and the client (Vite/
 * Vitest) without pulling in anything services/api-shaped. Only type-level
 * imports from `./signal-view` (the `SignalRow` shape both sides share).
 *
 * ## Why a page-load Gong check exists at all (§1.2 of the plan)
 *
 * `gong_flow_probe` is written by the 2-hourly `GongFlowProbeWorkflow` cron
 * and is the *base* verification state — `gong_verified_at` is the probe's
 * `verified_at` column, joined onto the row (list route: `p.verified_at AS
 * gong_verified_at`, `flow-signals-list.ts:89`; entity-signals: the join
 * `src/services/terra-signals/query.ts` adds, D-E). A rep can click a verb
 * button seconds after a contact left its flow but before the next probe
 * tick — B4's page-load live check re-verifies the rep's own rows on every
 * refresh and rewrites `gong_verified_at`, but a row's *saved* state is only
 * ever as fresh as its last check. `GONG_VERIFICATION_FRESH_MS` (one probe
 * interval) is the cutoff past which a `Running` row without a fresher
 * check is treated as `unverified` rather than `live_*` — Spencer's rule
 * (coordinator item B): no action verb renders until Gong has verified the
 * contact is actually still enrolled in that step, live.
 *
 * ## Evaluation order (nine states, first match wins)
 *
 * The nine states and their precedence are unchanged since B2; what
 * changed (fix-plan-batch2.md D-A / unit U1) is the SOURCE of the status
 * steps 3–6 read. Define an **observation** as a row carrying BOTH
 * `gong_verified_at` and `gong_probe_status` — the two wire fields both
 * routes compute from the SAME `gong_flow_probe` row. Then
 * `status = observed ? gong_probe_status : signal_status` (the snapshot
 * column is only the fallback when Gong has never been asked).
 *
 *   1. `action_state` `Archived` | `Completed` -> `closed` (terminal, no
 *      actions at all, regardless of flow status).
 *   2. `action_state` `Snoozed` -> `snoozed` (Bring back only, regardless of
 *      flow status — a snoozed row must always be un-snoozable).
 *   3. `status` `'No flow'` -> `no_flow` (view-only, no actions — there was
 *      never a Gong flow to check; only the snapshot can say this, since a
 *      probe row exists only for a real flow instance).
 *   4. `status` `'Removed from flow'` -> `removed`, but ONLY when the status
 *      is trusted: either it is not a probe observation at all (the
 *      snapshot-only path, which carries no probe timestamp to go stale), or
 *      it IS a probe observation and that probe is still fresh
 *      (`gong_verified_at` within `GONG_VERIFICATION_FRESH_MS`). A stale
 *      probe observation falls through to step 6, same as a stale `Running`
 *      probe does — Archive only, prefilled, when trusted.
 *   5. `status` `'Paused' | 'Pending' | 'Finished'` -> `paused`, gated by the
 *      SAME trust rule as step 4 (verified, not live — Archive only,
 *      feedback prefill naming the status; `'Finished'` is grouped with
 *      Paused/Pending, a documented lean carried over from `rowActions.ts`'s
 *      own precedent — Action View never sees an open `Finished` row today,
 *      but the Signal Action arm can in principle emit one).
 *   6. Anything else — `'Running'`, an unrecognized/`null` snapshot status,
 *      or a stale probe observation that steps 4/5 declined to trust
 *      — the view's `signal_status` no longer has an `ELSE 'Running'`
 *      catch-all (U22: unrecognised → `'Unknown'`, closed rows → NULL), so
 *      only a fresh `'Running'` observation is live):
 *      - no observation (either half missing), or `gong_verified_at`
 *        unparsable / older than `GONG_VERIFICATION_FRESH_MS` ->
 *        `unverified` (no verb — Gong has not recently confirmed this row
 *        is actually live). A half-observation (a timestamp without a
 *        status, or a status without a timestamp) is `unverified`, never
 *        live: fail closed.
 *      - else `signal_tactic` (case-insensitive) contains `'auto'` ->
 *        `live_auto` (auto steps self-progress in Gong; no verb).
 *      - else `signal_tactic` is blank/null -> `live_unknown_step` (verified
 *        live, but no step type to name a verb after — SFDC's own tactic
 *        field is unset).
 *      - else -> `live_manual` (verified live, real tactic -> a verb
 *        button).
 *
 * ## Why the pair, not the timestamp
 *
 * `signal_status` is the SNAPSHOT's column (`terra_signals_signal_action`),
 * evaluated by `STATUS_CASE` only when the snapshot is recomputed — after
 * the live check (fail-soft) or by the 10-minute sweep. `gong_verified_at`
 * is joined from the probe LIVE on every read. Pairing a fresh probe
 * timestamp with a possibly-stale snapshot status meant that, between a
 * probe write of `enrolled=false` and the recompute (or forever, if the
 * recompute failed), a row read `signal_status: 'Running'` +
 * `gong_verified_at: <now>` and resolved to `live_manual` — a Call button
 * for a contact Gong had just removed (fix-plan-batch2.md §1.1, finding
 * F1). The probe's own verdict (`gong_probe_status`, `gongProbeStatusSql`
 * in `arms/signal-action.ts`) and its timestamp are one observation; this
 * resolver never mixes one half of it with the snapshot.
 *
 * ## Divergences from Action View (`mini-apps/action-view/row-actions.ts`,
 * `merge-gong-enrichment.ts`, `grouping.ts:128-135`) — copied semantics,
 * never imported (mini-app boundary), and deliberately not identical:
 *
 *   1. Action View shows a verb on an unverified row (its `gongFlowUnverified`
 *      is treated as live — "batch failed" is optimistic). Spencer's rule B
 *      overrides this for Signals: no verb until `gong_verified_at` is fresh.
 *   2. Action View's `gongVerbLabel` falls back to `'Open in Gong'` for an
 *      unmatched tactic. Rule B removes that fallback entirely — `null`
 *      renders no verb, full stop, on both sides of the mini-app boundary
 *      now (`rowActions.ts` uses this file's `stepLabelFor`, which
 *      returns `string | null`).
 *   3. Action View chips render only in its grouping label and card
 *      (`'No step data'` / `'Removed from Flow'`) — it has no row-level
 *      chip because its table has no equivalent of the Flow status column.
 *      Signals rows keep that column; the chip this module derives is for
 *      the Signal card's meta line only (D-I) — not rendered a second time
 *      on the row.
 *   4. Action View's `'Finished'` is documented as never-seen-live (its
 *      merge only distinguishes running/stale/not-actionable); Signals
 *      treats it identically to Paused/Pending as a deliberate lean, not a
 *      gap — see point 5 above.
 *   5. `Snoozed -> Bring back` has no Action View equivalent — Snooze is a
 *      Signals-only affordance (Action View's nearest analog, "Mark read",
 *      is a different action entirely).
 */
import { z } from 'zod';
import { GONG_FLOW_END_STATUSES } from './rep-state';
import { SIGNAL_ACTION_STATUS_ORDER } from '../schemas/terra-signals';
import type { SignalRow } from './signal-view';

/**
 * The probe's own normalized verdict (fix-plan-batch2.md D-A): the status a
 * `gong_flow_probe` row implies, computed in SQL by `gongProbeStatusSql`
 * (`src/services/terra-signals/arms/signal-action.ts`) from the SAME probe
 * row as `gong_verified_at`, on both the list and entity-signals routes.
 * `null` when no probe row exists. Declared once here (the resolver is its
 * reader); `signal-view.ts` / `entity-signals.ts` extend their row schemas
 * with it.
 */
export const GongProbeStatusSchema = z.enum(SIGNAL_ACTION_STATUS_ORDER).exclude(['No flow']).nullable();
export type GongProbeStatus = z.infer<typeof GongProbeStatusSchema>;

/**
 * One probe interval (`GongFlowProbeWorkflow`'s cron fires at :15 every
 * even hour — see the workflow's own cron string), 2-hourly — Spencer,
 * 2026-09-24. The page-load live check (B4)
 * re-verifies the rep's own rows on every load/refresh anyway, so this only
 * governs the gap between probe ticks for rows the rep has not personally
 * refreshed (e.g. another rep's row on All Signals).
 */
export const GONG_VERIFICATION_FRESH_MS = 2 * 60 * 60 * 1000;

/**
 * Action View's own label for "no step type to show" (`grouping.ts:130,134`)
 * — reused verbatim here so the Signals group label, Step cell, and Signal
 * card chip all read identically to Action View's group label.
 */
export const NO_STEP_DATA_LABEL = 'No step data';

export type SignalVerificationState =
  | 'closed'
  | 'snoozed'
  | 'no_flow'
  | 'removed'
  | 'paused'
  | 'unverified'
  | 'live_auto'
  | 'live_manual'
  | 'live_unknown_step';

export type SignalVerificationInput = Pick<
  SignalRow,
  'action_state' | 'signal_status' | 'signal_tactic' | 'gong_verified_at' | 'gong_probe_status'
>;

/**
 * Is `verifiedAt` a fresh-enough probe/live-check timestamp as of `now`?
 * `null`/unparsable -> not fresh (never verified, or verified against a
 * corrupt value — fail closed to `unverified`, never to a false live).
 */
function isVerificationFresh(verifiedAt: string | null, now: Date): boolean {
  if (verifiedAt == null) return false;
  const verifiedMs = Date.parse(verifiedAt);
  if (Number.isNaN(verifiedMs)) return false;
  return now.getTime() - verifiedMs <= GONG_VERIFICATION_FRESH_MS;
}

/**
 * The status steps 3–6 of the resolver read: the probe's own verdict when
 * the row carries a whole observation (BOTH `gong_verified_at` and
 * `gong_probe_status`, one probe row), else the snapshot `signal_status`
 * (fix-plan-batch2.md D-A). Exported so a consumer that renders the resolved
 * state (the Signal card chip label / archive prefill in `rowActions.ts`)
 * names the SAME status the resolver decided on, never the snapshot's.
 */
export function isGongObservation(row: Pick<SignalVerificationInput, 'gong_verified_at' | 'gong_probe_status'>): boolean {
  return row.gong_verified_at != null && row.gong_probe_status != null;
}

export function effectiveSignalStatus(
  row: Pick<SignalVerificationInput, 'signal_status' | 'gong_verified_at' | 'gong_probe_status'>,
): string | null {
  return isGongObservation(row) ? row.gong_probe_status : row.signal_status;
}

/** Step 5's three verified-but-not-live statuses. */
export type PausedLikeSignalStatus = 'Paused' | 'Pending' | 'Finished';

export function isPausedLikeSignalStatus(status: string | null): status is PausedLikeSignalStatus {
  return status === 'Paused' || status === 'Pending' || status === 'Finished';
}

/**
 * Gong end statuses beyond `Finished` (`GONG_FLOW_END_STATUSES`), matched
 * case-insensitively on the trimmed value (same rule as
 * `normalizeGongFlowStatus`). Resolved like `Finished` (closed-like, step 5), but kept out of
 * `PausedLikeSignalStatus` so `rowActions.ts`'s `FLOW_STATUS_FEEDBACK` record
 * stays exhaustive; its type guard falls back to `unverifiedActions()` for
 * these until U26 gives them their own chip/feedback copy.
 */
export function isGongEndStatus(status: string | null): boolean {
  if (status === null) return false;
  const lower = status.trim().toLowerCase();
  return GONG_FLOW_END_STATUSES.some((s) => s.toLowerCase() === lower);
}

/**
 * The single verification-state resolver (D-A). See the module doc's
 * "Evaluation order" section for the full nine-state precedence — this
 * function is a direct transcription of that list, not a re-derivation.
 */
export function resolveSignalVerificationState(row: SignalVerificationInput, now: Date): SignalVerificationState {
  // 1. Terminal states — nothing to do, ever.
  if (row.action_state === 'Archived' || row.action_state === 'Completed') return 'closed';

  // 2. Snoozed — Bring back only, whatever the flow status says.
  if (row.action_state === 'Snoozed') return 'snoozed';

  // One observation = BOTH halves of the same probe row. A half-observation
  // is not an observation (fail closed — see "Why the pair, not the
  // timestamp"); without one, the snapshot's status is the only source.
  const observed = isGongObservation(row);
  const status = effectiveSignalStatus(row);

  // 3. No Gong flow at all -> view-only.
  if (status === 'No flow') return 'no_flow';

  // A probe observation is only trustworthy while fresh — mirrors the
  // Running branch's own gate (step 6). The snapshot-only path (no
  // observation) has no probe timestamp to go stale, so it is always
  // trusted. `probeFresh` further distinguishes "this IS an observation and
  // it's fresh" from "there's no observation at all", which step 6 needs
  // below (a stale observation must still fall through here, not re-trigger
  // trusted on the snapshot side).
  const probeFresh = observed && isVerificationFresh(row.gong_verified_at, now);
  const trusted = !observed || probeFresh;

  // 4. Removed from flow (Gong verified no active enrollment, and that
  // verdict is still trusted) -> Archive only. A stale probe verdict falls
  // through to step 6 -> unverified instead.
  if (status === 'Removed from flow' && trusted) return 'removed';

  // 5. Paused / Pending / Finished / Gong end statuses — verified (and trusted), not live.
  if ((isPausedLikeSignalStatus(status) || isGongEndStatus(status)) && trusted) return 'paused';

  // 6. 'Running' — verification-gated, and only by a whole, fresh
  // observation. Anything else reaching here (a stale probe verdict that
  // steps 4/5 declined to trust, 'Unknown', null, or an unrecognised string)
  // is 'unverified': the view no longer has an ELSE 'Running' catch-all to
  // mirror (U21), so an unrecognised status is never assumed live.
  if (!probeFresh || status !== 'Running') return 'unverified';

  const tactic = (row.signal_tactic ?? '').trim();
  if (tactic.toLowerCase().includes('auto')) return 'live_auto';
  if (!tactic) return 'live_unknown_step';
  return 'live_manual';
}

/**
 * Verb-name a `signal_tactic` value, or `null` for anything that is not
 * call / LinkedIn / manual email / task (D-C: never a fallback verb).
 * The single copy: the web `rowActions.ts` verb overlay imports it from
 * here (shared is imported by both server and client, never the reverse).
 * Used by the Step column cell and the Signal card's meta line (both B6/B7
 * consumers of this module).
 */
export function stepLabelFor(tactic: string | null | undefined): string | null {
  const s = (tactic ?? '').toLowerCase();
  if (!s) return null;
  if (s.includes('call')) return 'Call';
  if (s.includes('linkedin')) return s.includes('message') ? 'LinkedIn Message' : 'LinkedIn Connect';
  if (s.includes('email') && !s.includes('auto')) return 'Send Email';
  if (s.includes('task')) return 'Open Task';
  return null;
}
